import { useContactUsEmailStorage } from "@/components/support/contactUs/email/contact-us-email-storage";
import { cn } from "@/libs/utils";
import { ContactUsSchemaType } from "@/schemas/contact-us";
import { useTrans } from "@/utils/useTrans";
import { Button } from "@/v2-components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/v2-components/ui/form";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import TrashIcon from "@/v2-components/core/icon/trash-icon";
import { Input } from "@/v2-components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/v2-components/ui/select";
import { Fragment, useEffect } from "react";
import { useFormContext } from "react-hook-form";

export const gamePlatforms = [
  { id: "vr", label: "VR" },
  { id: "psvr2", label: "PSVR2" },
  { id: "pc", label: "PC" },
];

export default function GameRequests() {
  const { t } = useTrans(["support"]);

  const contactUsEmailStorage = useContactUsEmailStorage();

  const form = useFormContext<ContactUsSchemaType>();
  const gameRequests = form.watch("game-request");

  return (
    <div className="flex flex-col gap-6">
      {gameRequests?.map((_, index) => (
        <div
          key={index}
          className="relative flex flex-col gap-2 laptop:flex-row laptop:gap-6"
        >
          <FormField
            control={form.control}
            name={`game-request.${index}.title`}
            render={({ field }) => (
              <FormItem className="w-full space-y-2">
                <FormLabel variant="text">
                  <span>{t("Title")}</span>
                  <span>{" *"}</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("Game Title")}
                    {...field}
                    onBlur={() => {
                      field.onBlur();
                      contactUsEmailStorage.set(form.getValues());
                    }}
                  />
                </FormControl>
                <FormMessage t={t} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`game-request.${index}.platform`}
            render={({ field }) => (
              <FormItem className="w-full space-y-2">
                <FormLabel variant="select">
                  <span>{t("Platform")}</span>
                  <span>{" *"}</span>
                </FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    contactUsEmailStorage.set(form.getValues());
                  }}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger variant="form" ref={field.ref}>
                      <SelectValue placeholder={t("Choose a platform")} />
                      <ChevronDownIcon />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {gamePlatforms.map((platform, index) => (
                      <Fragment key={platform.id}>
                        <SelectItem value={platform.id}>
                          {platform.label}
                        </SelectItem>
                        <SelectSeparator
                          className={cn(
                            index === gamePlatforms.length - 1 && "hidden",
                          )}
                        />
                      </Fragment>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage t={t} />
              </FormItem>
            )}
          />

          {index !== 0 && (
            <Button
              type="button"
              className="mt-3 self-end p-1 laptop:absolute laptop:-right-10 laptop:top-10 laptop:mt-0"
              variant="darkgray-text"
              size="none"
              onClick={(e) => {
                e.preventDefault();
                form.setValue(
                  "game-request",
                  gameRequests.filter((_, i) => i !== index),
                );
              }}
            >
              <TrashIcon className="h-6 w-6" />
            </Button>
          )}
        </div>
      ))}

      <Button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          form.setValue("game-request", [
            ...(gameRequests || []),
            { title: "", platform: "" },
          ]);
        }}
        variant="blue-outline"
        shape="pill"
        size={"sm"}
        className="w-[72px]"
      >
        {t("Add")}
      </Button>
    </div>
  );
}
